import React, { useState } from 'react';
import { CHAINS } from '../../config/constants';
import { TRANSACTION_STATUS } from '../../utils/transactionManager';
import { formatAddress, formatTokenAmount, formatTime, formatTxHash } from '../../utils/format';
import { 
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

const TransactionRow = ({ transaction }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedHash, setCopiedHash] = useState(null);

  const fromChain = Object.values(CHAINS).find(c => c.id === transaction.fromChain);
  const toChain = Object.values(CHAINS).find(c => c.id === transaction.toChain);

  const getStatusIcon = (status) => {
    switch (status) {
      case TRANSACTION_STATUS.COMPLETED:
        return <CheckCircleIcon className="w-5 h-5 text-success-500" />;
      case TRANSACTION_STATUS.FAILED:
      case TRANSACTION_STATUS.EXPIRED:
        return <XCircleIcon className="w-5 h-5 text-error-500" />;
      case TRANSACTION_STATUS.PENDING:
      case TRANSACTION_STATUS.CONFIRMED:
        return <ClockIcon className="w-5 h-5 text-warning-500 animate-pulse" />;
      default:
        return <ExclamationCircleIcon className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case TRANSACTION_STATUS.PENDING:
        return 'Pending';
      case TRANSACTION_STATUS.CONFIRMED:
        return 'Confirmed';
      case TRANSACTION_STATUS.COMPLETED:
        return 'Completed';
      case TRANSACTION_STATUS.FAILED:
        return 'Failed';
      case TRANSACTION_STATUS.EXPIRED:
        return 'Expired';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TRANSACTION_STATUS.COMPLETED:
        return 'text-success-600 bg-success-100 dark:bg-success-900/20';
      case TRANSACTION_STATUS.PENDING:
      case TRANSACTION_STATUS.CONFIRMED:
        return 'text-warning-600 bg-warning-100 dark:bg-warning-900/20';
      case TRANSACTION_STATUS.FAILED:
      case TRANSACTION_STATUS.EXPIRED:
        return 'text-error-600 bg-error-100 dark:bg-error-900/20';
      default:
        return 'text-slate-600 bg-slate-100 dark:bg-slate-800';
    }
  };

  const getExplorerUrl = (txHash, chainId) => {
    const chain = Object.values(CHAINS).find(c => c.id === chainId);
    return chain ? `${chain.explorerUrl}/tx/${txHash}` : '#';
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedHash(type);
      setTimeout(() => setCopiedHash(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
        {/* Transaction Hash */}
        <div className="col-span-2">
          <div className="flex items-center space-x-2 group">
            <div 
              className="font-mono text-sm cursor-pointer" 
              title={transaction.txHash}
            >
              {formatTxHash(transaction.txHash)}
            </div>
            <button
              onClick={() => copyToClipboard(transaction.txHash, 'main')}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all"
              title={copiedHash === 'main' ? 'Copied!' : 'Copy full transaction hash'}
            >
              <DocumentDuplicateIcon className="w-3 h-3" />
            </button>
          </div>
          
        </div>

        {/* Route */}
        <div className="col-span-2">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: fromChain?.color }}
              />
              <span className="text-sm">{fromChain?.symbol}</span>
            </div>
            <ArrowRightIcon className="w-3 h-3 text-slate-400" />
            <div className="flex items-center space-x-1">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: toChain?.color }}
              />
              <span className="text-sm">{toChain?.symbol}</span>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="col-span-2">
          <div className="font-medium">
            {formatTokenAmount(transaction.amount)} PFVS
          </div>
          {transaction.feeUsd && (
            <div className="text-xs text-slate-500">
              Fee: ${transaction.feeUsd}
            </div>
          )}
        </div>

        {/* Status */}
        <div className="col-span-2">
          <div className="flex items-center space-x-2">
            {getStatusIcon(transaction.status)}
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                transaction.status
              )}`}
            >
              {getStatusText(transaction.status)}
            </span>
          </div>
        </div>

        {/* Time */}
        <div className="col-span-2">
          <div className="text-sm">
            {formatTime(transaction.timestamp, 'relative')}
          </div>
          <div className="text-xs text-slate-500">
            {formatTime(transaction.timestamp, 'absolute')}
          </div>
        </div>

        {/* Actions */}
        <div className="col-span-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
              title="View Details"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <a
              href={getExplorerUrl(transaction.txHash, transaction.fromChain)}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
              title="View in Explorer"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 group">
            <div 
              className="font-mono text-sm cursor-pointer" 
              title={transaction.txHash}
            >
              {formatTxHash(transaction.txHash)}
            </div>
            <button
              onClick={() => copyToClipboard(transaction.txHash, 'mobile')}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all"
              title={copiedHash === 'mobile' ? 'Copied!' : 'Copy full transaction hash'}
            >
              <DocumentDuplicateIcon className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(transaction.status)}
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                transaction.status
              )}`}
            >
              {getStatusText(transaction.status)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: fromChain?.color }}
              />
              <span className="text-sm">{fromChain?.symbol}</span>
            </div>
            <ArrowRightIcon className="w-3 h-3 text-slate-400" />
            <div className="flex items-center space-x-1">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: toChain?.color }}
              />
              <span className="text-sm">{toChain?.symbol}</span>
            </div>
          </div>
          <div className="font-medium">
            {formatTokenAmount(transaction.amount)} PFVS
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-400">
          <span>{formatTime(transaction.timestamp, 'relative')}</span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <a
              href={getExplorerUrl(transaction.txHash, transaction.fromChain)}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Transaction Details</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">From Chain:</span>
                  <span>{fromChain?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">To Chain:</span>
                  <span>{toChain?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Amount:</span>
                  <span>{formatTokenAmount(transaction.amount)} PFVS</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Recipient:</span>
                  <div className="flex items-center space-x-2 group">
                    <span className="font-mono" title={transaction.recipient}>
                      {transaction.recipient}
                    </span>
                    <button
                      onClick={() => copyToClipboard(transaction.recipient, 'recipient')}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all"
                      title={copiedHash === 'recipient' ? 'Copied!' : 'Copy recipient address'}
                    >
                      <DocumentDuplicateIcon className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Status Information</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Status:</span>
                  <span>{getStatusText(transaction.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Created:</span>
                  <span>{formatTime(transaction.timestamp, 'absolute')}</span>
                </div>
                {transaction.completedAt && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Completed:</span>
                    <span>{formatTime(transaction.completedAt, 'absolute')}</span>
                  </div>
                )}
                {transaction.targetTxHash && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Target Tx:</span>
                    <div className="flex items-center space-x-2 group">
                      <a
                        href={getExplorerUrl(transaction.targetTxHash, transaction.toChain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                        title={transaction.targetTxHash}
                      >
                        {transaction.targetTxHash}
                      </a>
                      <button
                        onClick={() => copyToClipboard(transaction.targetTxHash, 'target')}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all"
                        title={copiedHash === 'target' ? 'Copied!' : 'Copy target transaction hash'}
                      >
                        <DocumentDuplicateIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionRow; 